import { Controller } from "@hotwired/stimulus"

type Fee = { name: string; amount_cents: number; stripe_product_id?: string }

// Manages a dynamic list of fees. The picker is a Tom Select over the Stripe
// fee products, with `create` on so a new name can be typed in; either way
// the choice becomes a row straight away, with the product's default price
// prefilled and editable in the row. Rows with an amount are stored as JSON
// in a hidden field for submission.
//
// Usage:
//   <div data-controller="dynamic-fees" data-dynamic-fees-fees-value='[...]'>
//     <select data-controller="tom-select" data-dynamic-fees-target="picker"
//             data-action="tom-select:item-added->dynamic-fees#addFee">
//       <option value="prod_x" data-name="Onboarding" data-amount-cents="100000">Onboarding</option>
//     </select>
//     <div data-dynamic-fees-target="list"></div>
//     <input type="hidden" data-dynamic-fees-target="output" name="deal[fees]" />
//   </div>
export default class extends Controller {
  static targets = ["list", "picker", "output"]
  static values = {
    fees: { type: Array, default: [] },
    amountPlaceholder: { type: String, default: "0.00" },
  }

  declare readonly listTarget: HTMLElement
  declare readonly pickerTarget: HTMLSelectElement
  declare readonly outputTarget: HTMLInputElement
  declare feesValue: Fee[]
  declare amountPlaceholderValue: string

  private fees: Fee[] = []

  connect(): void {
    this.fees = [...this.feesValue]
    this.render()
  }

  // Called when the data attribute changes externally
  feesValueChanged(): void {
    if (this.element.isConnected) {
      this.fees = [...this.feesValue]
      this.render()
    }
  }

  // A product option carries data-name; a name Tom Select created on the fly
  // has no option data and is used as typed.
  addFee(event: CustomEvent): void {
    const value = String(event.detail?.value ?? "")
    if (!value) return

    const option = Array.from(this.pickerTarget.options).find((o) => o.value === value)
    const productName = option?.dataset.name
    const cents = parseInt(option?.dataset.amountCents || "", 10)
    this.fees.push({
      ...(productName ? { name: productName, stripe_product_id: value } : { name: value }),
      amount_cents: Number.isFinite(cents) ? cents : 0,
    })
    this.render()

    // Tom Select finishes its own add (caret, state) after item_add fires;
    // reset it once it has, or it takes focus straight back.
    setTimeout(() => {
      this.tomSelect()?.clear(true)
      this.tomSelect()?.close()
      // A product arrives with its price already right, so taking the caret
      // there only puts a correct number one keystroke from being wiped. Only
      // a name typed in by hand leaves an amount still to enter.
      const inputs = this.amountInputs()
      if (this.fees[this.fees.length - 1]?.amount_cents > 0) return

      inputs[inputs.length - 1]?.focus()
    })
  }

  updateAmount(event: Event): void {
    const input = event.target as HTMLInputElement
    const index = this.indexOf(input)
    // Users type dollars (e.g. 500.00); we store integer cents on the wire so
    // the backend's amount_cents column stays authoritative. Math.round
    // guards against float drift from 0.01 increments.
    const dollars = parseFloat(input.value)
    this.fees[index].amount_cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0
    if (this.fees[index].amount_cents > 0) input.classList.remove("input-error")
    this.syncOutput()
  }

  // A row with no amount is dropped on save, so it has to be marked — but only
  // once the user has left it, not while a brand-new row sits there untouched
  // or while an amount is still half-typed.
  flagEmptyAmount(event: Event): void {
    const input = event.target as HTMLInputElement
    // Removing a row blurs its input on the way out, by which point the fee is
    // already spliced away — an absent one has nothing left to flag.
    const fee = this.fees[this.indexOf(input)]
    if (fee) input.classList.toggle("input-error", fee.amount_cents <= 0)
  }

  remove(event: Event): void {
    event.preventDefault()
    this.fees.splice(this.indexOf(event.target as HTMLElement), 1)
    this.render()
  }

  render(): void {
    if (this.fees.length === 0) {
      this.listTarget.innerHTML = ""
      this.syncOutput()
      return
    }

    this.listTarget.innerHTML = `
      <table class="table table-sm w-full bg-base-200 rounded-lg">
        <tbody>
          ${this.fees
            .map(
              (fee, index) => `
            <tr data-index="${index}">
              <td class="text-sm">${this.escapeHtml(fee.name)}</td>
              <td class="text-right">${this.amountField(fee)}</td>
              <td class="w-8 text-right">
                <button type="button" class="btn btn-ghost btn-xs btn-circle"
                  data-action="dynamic-fees#remove">&times;</button>
              </td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>`

    this.syncOutput()
  }

  private amountField(fee: Fee): string {
    const value = fee.amount_cents > 0 ? (fee.amount_cents / 100).toFixed(2) : ""

    return `<span class="text-gray-500 mr-1">$</span><input type="number" min="0" step="0.01" value="${value}"
              class="input input-sm input-bordered w-28"
              placeholder="${this.escapeHtml(this.amountPlaceholderValue)}"
              data-action="input->dynamic-fees#updateAmount blur->dynamic-fees#flagEmptyAmount">`
  }

  private indexOf(element: HTMLElement): number {
    return parseInt(element.closest("[data-index]")?.getAttribute("data-index") || "0", 10)
  }

  private amountInputs(): HTMLInputElement[] {
    return Array.from(this.listTarget.querySelectorAll("input"))
  }

  // Tom Select stamps its instance on the element it took over.
  private tomSelect(): { clear: (silent?: boolean) => void; close: () => void } | undefined {
    return (this.pickerTarget as { tomselect?: { clear: (silent?: boolean) => void; close: () => void } }).tomselect
  }

  // Rows are written straight into the hidden field, which fires no input
  // event of its own — anything pricing off the fees needs telling.
  private syncOutput(): void {
    this.outputTarget.value = JSON.stringify(this.fees.filter((fee) => fee.amount_cents > 0))
    this.dispatch("changed")
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
